import { gql } from '@apollo/client';
import { Checkbox, FormControl, FormLabel, Text, useToast } from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormLabelPendingIndicator,
  isPropertyPending,
} from '~components/ui/FormLabelPendingIndicator';
import { BatteryPowerManagement } from '~graphql/__generated__/types';
import { useAnalyticsReporter } from '~utils/analytics';
import { fromError } from '~utils/errors';
import {
  DisplayBatteryManagementToggle_DisplayFragment,
  useBulkUpdateBatteryPowerManagementMutation,
} from './__generated__/DisplayBatteryManagement.graphql';

interface Props {
  display: DisplayBatteryManagementToggle_DisplayFragment;
  isDisabled?: boolean;
}

const getInitialEnabled = (display: DisplayBatteryManagementToggle_DisplayFragment) => {
  if (isPropertyPending(display.battery)) {
    return display.battery?.desired?.powerManagement === BatteryPowerManagement.On;
  }
  return display.battery?.reported?.powerManagement === BatteryPowerManagement.On;
};

export function DisplayBatteryManagementToggle({ display, isDisabled = false }: Props) {
  const [isEnabled, setIsEnabled] = useState<boolean>(getInitialEnabled(display));
  const analytics = useAnalyticsReporter();
  const { t } = useTranslation();
  const toast = useToast();
  const [updateBatteryPowerManagement] = useBulkUpdateBatteryPowerManagementMutation();

  useEffect(() => {
    if (isPropertyPending(display.battery)) {
      setIsEnabled(display.battery?.desired?.powerManagement === BatteryPowerManagement.On);
    } else {
      setIsEnabled(display.battery?.reported?.powerManagement === BatteryPowerManagement.On);
    }
  }, [display]);

  const handleUpdate = useCallback(
    async (value: boolean) => {
      setIsEnabled(value);
      analytics.track('displaySettingUpdate', { group: 'power', changeItem: 'batteryManagement' });

      try {
        await updateBatteryPowerManagement({
          variables: {
            input: {
              displayIds: [display.id],
              powerManagement: value ? BatteryPowerManagement.On : BatteryPowerManagement.Off,
            },
          },
        });
      } catch (err) {
        setIsEnabled(!value);
        toast({
          status: 'error',
          title: t('batteryManagementErr'),
          description: fromError(err, 'UpdateBatteryPowerManagement'),
        });
      }
    },
    [analytics, display, toast, updateBatteryPowerManagement, t],
  );

  return (
    <FormControl>
      <FormLabel>
        {t('batteryManagement')}
        <FormLabelPendingIndicator isPending={isPropertyPending(display.battery)} />
      </FormLabel>

      <Text color="gray.500" mb="2">
        {t('batteryManagementText')}
      </Text>
      <Checkbox
        isChecked={isEnabled}
        onChange={(e) => handleUpdate(e.target.checked)}
        isDisabled={isDisabled}
      >
        {t('enableBatteryManagement')}
      </Checkbox>
    </FormControl>
  );
}

DisplayBatteryManagementToggle.graphql = {
  fragments: {
    DisplayBatteryManagementToggle_display: gql`
      fragment DisplayBatteryManagementToggle_display on Display {
        id
        battery {
          desired {
            powerManagement
          }
          reported {
            powerManagement
          }
        }
      }
    `,
  },
  mutations: {
    BulkUpdateBatteryPowerManagement: gql`
      mutation BulkUpdateBatteryPowerManagement(
        $input: DisplayBulkUpdateBatteryPowerManagementInput!
      ) {
        displayBulkUpdateBatteryPowerManagement(input: $input) {
          displays {
            id
            ...DisplayBatteryManagementToggle_display
          }
        }
      }
    `,
  },
};
